import apiClient from "../lib/api-client";

export interface Doctor {
  id: string;
  tenantId: string;
  doctorCode: string;
  doctorName: string;
  qualifications?: string;
  specialization?: string;
  registrationNumber: string;
  registrationBody?: string;
  registrationExpiry?: string;
  hospitalId?: string;
  hospital?: {
    id: string;
    hospitalName: string;
  };
  clinicName?: string;
  clinicAddress?: string;
  phone?: string;
  email?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    prescriptions: number;
    invoices: number;
  };
}

export interface CreateDoctorDto {
  doctorCode: string;
  doctorName: string;
  qualifications?: string;
  specialization?: string;
  registrationNumber: string;
  registrationBody?: string;
  registrationExpiry?: string;
  hospitalId?: string;
  clinicName?: string;
  clinicAddress?: string;
  phone?: string;
  email?: string;
}

export interface UpdateDoctorDto extends Partial<CreateDoctorDto> {
  isActive?: boolean;
}

export interface DoctorListResponse {
  data: Doctor[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface DoctorSearchResult {
  id: string;
  doctorCode: string;
  doctorName: string;
  registrationNumber: string;
  specialization?: string;
  phone?: string;
}

export const doctorService = {
  async getAll(params?: {
    page?: number;
    limit?: number;
    search?: string;
    specialization?: string;
    hospitalId?: string;
    isActive?: boolean;
  }): Promise<DoctorListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append("page", params.page.toString());
    if (params?.limit) searchParams.append("limit", params.limit.toString());
    if (params?.search) searchParams.append("search", params.search);
    if (params?.specialization)
      searchParams.append("specialization", params.specialization);
    if (params?.hospitalId)
      searchParams.append("hospitalId", params.hospitalId);
    if (params?.isActive !== undefined)
      searchParams.append("isActive", params.isActive.toString());

    const response = await apiClient.get(`/doctors?${searchParams}`);
    return response.data;
  },

  async getById(id: string): Promise<Doctor> {
    const response = await apiClient.get(`/doctors/${id}`);
    return response.data;
  },

  async create(data: CreateDoctorDto): Promise<Doctor> {
    const response = await apiClient.post("/doctors", data, {
      headers: { "Idempotency-Key": crypto.randomUUID() },
    });
    return response.data;
  },

  async update(id: string, data: UpdateDoctorDto): Promise<Doctor> {
    const response = await apiClient.patch(`/doctors/${id}`, data, {
      headers: { "Idempotency-Key": crypto.randomUUID() },
    });
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/doctors/${id}`, {
      headers: { "Idempotency-Key": crypto.randomUUID() },
    });
  },

  async search(query: string, limit?: number): Promise<DoctorSearchResult[]> {
    const searchParams = new URLSearchParams();
    searchParams.append("q", query);
    if (limit) searchParams.append("limit", limit.toString());

    const response = await apiClient.get(`/doctors/search?${searchParams}`);
    return response.data;
  },
};
